import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Grid } from '@material-ui/core';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import clsx from 'clsx';
import { useQueryParams } from '@moderepo/mode-app-components';
import { fetchRobotById, rcDataContext, selectRobotById } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../../utils/customHooks';
import { FileRequestsTableHiddenFields, RCFileRequestsListingContainer } from './RCFileRequestsListingContainer';


/**
 * Create the list of fields we don't want to show in the file requests table. This is static so we can define it outside of
 * the component
 */
const fileRequestsTableHiddenFields: FileRequestsTableHiddenFields = {
    homeId        : true,
    errorMessage  : true,
    updatedAt     : true,
    expirationDate: true,
    target        : true,
};



/**
 * This page Display ALL a File Requests for a home
 */
export const RCFileRequestsPage = () => {
    // Get the params from the route or url
    const params = useParams();
    const queryParams = useQueryParams();
    const projectId = Number(params.projectId);

    /**
     * We need the homeId deviceId to make API calls but there is no guarantee the homeId and deviceId will be in the route params, it depends on
     * which route the user is on. Sometime the homeId will be passed as URL param instead so we need to try to get the homeId from either
     * routeParam or urlParam. And if we don't have homeId from either but we have deviceId, we can also lookup homeId from the device.
     * As for deviceId, sometime the deviceId is not in the route params but we robotId instead. For this case, we will need to fetch the robot data
     * and get the deviceId from the robot data instead.
     *      /projectId/robotId?homeId=1234
     *      /projectId/deviceId
     *      /projectId/deviceId/robotId?homeId=1234
     *      /projectId/userId/homeId/robotId
     *      /projectId/userId/homeId/deviceId/robotId
     */
    const homeId = params.homeId || queryParams.get('homeId') ? Number(params.homeId || queryParams.get('homeId')) : undefined;
    const deviceId = params.deviceId ? Number(params.deviceId) : undefined;
    const robotId = params.robotId ? params.robotId.toString() : undefined;


    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);

    const device = deviceId ? selectProjectDeviceById(dataState, deviceId) : undefined;
    const robot = robotId ? selectRobotById(rcDataState, homeId, robotId) : undefined;


    /**
     * This useEffect is used for fetching Device data for the 'deviceId' if we don't have the device data yet
     */
    useEffect(() => {
        if (!device && deviceId) {
            dataDispatch(fetchProjectDeviceById(projectId, deviceId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [dataDispatch, device, deviceId, projectId]);


    /**
     * This useEffect is used for fetching Robot data for the 'robotId' if we don't have the robot data yet
     * NOTE: we only need to fetch robot if we don't have deviceId
     */
    useEffect(() => {
        if (!robot && !deviceId && homeId && robotId) {
            rcDataDispatch(fetchRobotById(projectId, homeId, robotId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [deviceId, homeId, projectId, rcDataDispatch, robot, robotId]);



    // Get the homeId and deviceId from different and sources that we can use
    const theHomeId = homeId || device?.homeId || robot?.homeId;
    const theDeviceId = deviceId || device?.id || robot?.deviceId;



    return (
        <div className={clsx('page-main-content')}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.file_requests')}
                    </div>
                    <div className="page-subtitle">
                        {trans('page.subtitle.file_requests')}
                    </div>
                </div>
            </div>

            {projectId && theHomeId && theDeviceId ? (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <RCFileRequestsListingContainer
                            projectId={projectId}
                            homeId={theHomeId}
                            deviceId={theDeviceId}
                            robotId={robotId}
                            hiddenFields={fileRequestsTableHiddenFields}
                        />
                    </Grid>
                </Grid>
            ) : (
                <CircularProgress />
            )}

        </div>
    );
};
